import * as accountAPI from "@/api/account";
import { setAccount, getAccount } from "@/utils/auth";
import { getIsAccessDisabled } from "@/utils/permissions";

const state = {
  usage: {},
  account: getAccount() ? JSON.parse(getAccount()) : {},
  subList: JSON.parse(localStorage.getItem("subList") || "[]"),
  creditCardList: [],
  invoiceList: [],
  profileUsers: {},
  pendingProfileUsers: {},
  fullProfileUsers: [],
  onboarding_progress: {},
};

const mutations = {
  setProfileUsers(state, profileUsers) {
    state.profileUsers = profileUsers;
  },
  setPendingProfileUsers(state, pendingProfileUsers) {
    state.pendingProfileUsers = pendingProfileUsers;
  },
  setProfileFullUsers(state, fullUsers) {
    state.fullProfileUsers = fullUsers;
  },
  setUsage(state, usage) {
    state.usage = usage;
  },
  setAccount(state, account) {
    account ? (state.account = account) : (state.account = {});
    setAccount(JSON.stringify(state.account));
  },
  setSubList(state, subList) {
    subList ? (state.subList = subList) : (state.subList = []);
    localStorage.setItem("subList", JSON.stringify(state.subList));
  },
  setCreditCardList(state, creditList) {
    state.creditCardList = creditList;
  },
  setInvoiceList(state, invoiceList) {
    state.invoiceList = invoiceList;
  },
  setOnboardingProgress(state, onboarding_progress) {
    state.onboarding_progress = onboarding_progress;
  },
};

// Free-tier feature entitlements read straight off the subscription — value-driven,
// never a plan-name string compare (paid plans leave the caps null/0 = unlimited).
const getters = {
  // Cap on the expansion date-window filter, in days. null = unlimited.
  expansionWindowMaxDays(state) {
    const v = Number(state.account?.subscription?.max_expansion_window_days);
    return v > 0 ? v : null;
  },
  // Whether the plan can create API keys.
  isApiAccessAvailable(state) {
    return state.account?.subscription?.is_api_access_available === true;
  },
  // Whether the plan can see company employee data (headcount + people list).
  // Fail-open (only false locks) so paid users never flash a lock before the plan resolves.
  isExpansionEmployeesAvailable(state) {
    return (
      state.account?.subscription?.is_expansion_employees_available !== false
    );
  },
  // The subscription detail is loaded lazily (getProfile). Gates that would
  // otherwise fail-closed (e.g. the API-key lock) check this so a paid user
  // never sees a "locked" flash before their plan resolves.
  isSubscriptionLoaded(state) {
    return !!state.account?.subscription;
  },
};

const actions = {
  async getProfile({ commit, dispatch }, data) {
    const { profile_id, dontGetCommonData } = data;
    const res = await accountAPI.getProfile({ profile_id }).catch((err) => {
      throw err;
    });
    commit("common/setNotificationBar", res?.data?.failure_charges.length > 0, {
      root: true,
    });
    commit("setAccount", res?.data);
    commit("setOnboardingProgress", res?.data?.onboarding_progress || {});
    // Keep the trial banner / trial-ended gate in sync with the subscription payload.
    const subscription = res?.data?.subscription;
    commit(
      "user/setTrialState",
      {
        is_trial: subscription?.is_trial === true,
        is_trial_expired: subscription?.is_trial_expired === true,
        prepaid_until: subscription?.prepaid_until || null,
      },
      { root: true },
    );
    if (!dontGetCommonData) {
      dispatch("common/getDepartments", {}, { root: true });
      dispatch("common/getCompanyList", "", { root: true });
      dispatch("common/getCompanyListMap", "", { root: true });
      dispatch("common/getDepartmentFunctions", {}, { root: true });
      dispatch("common/getLocationList", {}, { root: true });
      dispatch("common/getIndustry", {}, { root: true });
      dispatch("common/getIndustryCategory", {}, { root: true });
      dispatch("common/getTechList", {}, { root: true });
      dispatch("common/getCategoryList", {}, { root: true });
      dispatch("common/getMediaList", {}, { root: true });
      dispatch("common/getRevenue", {}, { root: true });
      dispatch("common/getCompanySize", {}, { root: true });
      dispatch("common/getManagementLevels", {}, { root: true });
      dispatch("common/getAdList", {}, { root: true });
      dispatch("common/getSequenceList", {}, { root: true });
      dispatch("common/getVerticalCategoryList", {}, { root: true });
      dispatch("common/getNewsCategorysList", {}, { root: true });
    }
    return res?.data;
  },
  async getUsage(context, data) {
    const usage = await accountAPI.getUsage(data).catch((err) => {
      throw err;
    });
    context.commit("setUsage", usage?.data);
    return usage?.data;
  },
  async updateUser(context, data) {
    const res = await accountAPI.updateUser(data).catch((err) => {
      console.log(err);
      throw err;
    });
    return res?.data;
  },
  async updatePassword(context, data) {
    const res = await accountAPI
      .updatePassword({ ...data, access_token: context.rootState.user.token })
      .catch((err) => {
        console.log(err);
        throw err;
      });
    return res?.data;
  },
  /*--------------profile-user-------------------*/
  async getUserList(context, data) {
    try {
      if (getIsAccessDisabled(context.rootState?.user?.user_access_code)) {
        return;
      }
      const currentId = this.state.user.currentId.profile_id;
      const params = {
        profile_id: currentId,
      };
      const res = await accountAPI
        .getProfileUsers({
          ...params,
          ...data,
        })
        .catch((err) => {
          console.log(err);
          throw err;
        });
      if (data && data.is_current) {
        context.commit("setProfileUsers", res?.data);
      } else {
        context.commit("setPendingProfileUsers", res?.data);
      }
      return res?.data;
    } catch (error) {
      console.log(error);

      const code = error?.response?.data?.code;
      context.commit("user/set_user_access_code", +code, {
        root: true,
      });
      throw error;
    }
  },
  async getFullUserList(context, data) {
    const currentId = this.state.user.currentId.profile_id;
    const params = {
      profile_id: currentId,
    };
    const res = await accountAPI
      .getProfileFullUsers({
        ...params,
        ...data,
      })
      .catch((err) => {
        console.log(err);
        throw err;
      });
    context.commit("setProfileFullUsers", res?.data);
    return res?.data;
  },
  async inviteUsers(context, data) {
    const res = await accountAPI.inviteProfileUser(data).catch((err) => {
      console.log(err);
      throw err;
    });
    return res?.data;
  },
  async deleteUsers(context, data) {
    const res = await accountAPI.deleteUser(data).catch((err) => {
      console.log(err);
      throw err;
    });
    return res?.data;
  },

  /*------------订阅------------------ */
  async getSubscription(context, data) {
    try {
      if (
        getIsAccessDisabled(context.rootState.user.subscription_access_code)
      ) {
        return;
      }

      const res = await accountAPI.getSubscription(data).catch((err) => {
        console.log(err);
        throw err;
      });
      context.commit("setSubList", res);
      return res;
    } catch (error) {
      const code = error?.response?.data?.code;

      context.commit("user/set_subscription_access_code", +code, {
        root: true,
      });
    }
  },
  /*--订阅grade--*/
  async upgradeSubscription(context, data) {
    const res = await accountAPI.upgradeSubscription(data).catch((err) => {
      console.log(err);
      throw err;
    });
    return res?.data;
  },
  /*--购买credit--*/
  async purchaseCredit(context, data) {
    const res = await accountAPI.purchaseCredit(data).catch((err) => {
      console.log(err);
      throw err;
    });
    return res?.data;
  },

  /*------------billing------------------*/
  /*--增加信用卡--*/
  async addCreditCard(context, data) {
    const unused_res = await accountAPI.addCredit(data).catch((err) => {
      console.log(err);
      throw err;
    });
  },
  async getCreditCard(context, data) {
    try {
      if (
        getIsAccessDisabled(context.rootState.user.payment_method_access_code)
      ) {
        return;
      }
      const currentId = this.state.user.currentId.profile_id;
      const params = {
        profile_id: currentId,
      };
      const res = await accountAPI.getPaymentMethods({ ...params, ...data });
      context.commit("setCreditCardList", res?.data);
      return res?.data;
    } catch (error) {
      console.log(error);
      const code = error?.response?.data?.code;
      context.commit("user/set_payment_method_access_code", +code, {
        root: true,
      });
      throw error;
    }
  },
  /*--更新（删除）信用卡--*/
  async deleteCreditCard(context, data) {
    const res = await accountAPI.deleteCredit(data).catch((err) => {
      console.log(err);
      throw err;
    });
    return res?.data;
  },
  /*----下载发票----*/
  async downloadInvoice(context, data) {
    const res = await accountAPI.downloadInvoice(data).catch((err) => {
      console.log(err);
      throw err;
    });
    return res?.data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
